// O editor pode reinjetar este asset; a própria declaração precisa ficar protegida.
if (!customElements.get('video-review-carousel')) {
  /**
   * Controla a esteira contínua e o modal de avaliações em vídeo.
   * Cada instância é isolada para permitir várias seções na mesma página.
   */
  class VideoReviewCarousel extends HTMLElement {
  /** Localiza os elementos internos e registra eventos da instância. */
  connectedCallback() {
    this.viewport = this.querySelector('[data-video-review-viewport]');
    this.track = this.querySelector('[data-video-review-track]');
    this.sourceGroup = this.querySelector('[data-video-review-source-group]');
    this.modal = this.querySelector('[data-video-review-modal]');
    this.modalContent = this.querySelector('[data-video-review-modal-content]');
    this.modalCaption = this.querySelector('[data-video-review-modal-caption]');
    this.modalCounter = this.querySelector('[data-video-review-modal-counter]');
    this.previousButton = this.querySelector('[data-video-review-previous]');
    this.nextButton = this.querySelector('[data-video-review-next]');

    // Uma renderização incompleta do editor não deve registrar listeners inválidos.
    if (!this.viewport || !this.track || !this.sourceGroup || !this.modal) return;

    this.handleTrackClick = this.handleTrackClick.bind(this);
    this.handleModalClick = this.handleModalClick.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.track.addEventListener('click', this.handleTrackClick);
    this.modal.addEventListener('click', this.handleModalClick);
    this.modal.addEventListener('close', this.handleModalClose);
    this.querySelector('[data-video-review-close]')?.addEventListener('click', this.handleModalClose);
    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    document.addEventListener('keydown', this.handleKeyDown);

    // ResizeObserver mantém a largura dos cards correta após mudanças no editor.
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.viewport);
    }
    else {
      // Navegadores antigos recebem um fallback de resize global.
      window.addEventListener('resize', this.handleResize);
    }

    this.rebuild();
  }

  /** Remove listeners e observadores quando a Shopify recarrega a seção. */
  disconnectedCallback() {
    this.track?.removeEventListener('click', this.handleTrackClick);
    this.modal?.removeEventListener('click', this.handleModalClick);
    this.modal?.removeEventListener('close', this.handleModalClose);
    this.querySelector('[data-video-review-close]')?.removeEventListener('click', this.handleModalClose);
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.handleResize);
    this.resizeObserver?.disconnect();

    // Um quadro pendente não pode atuar sobre uma seção já removida.
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);

    this.releasePageScroll();
  }

  /** Agrupa várias mudanças de tamanho em uma reconstrução por quadro. */
  handleResize() {
    // Apenas a medição mais recente é necessária.
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);

    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.rebuild();
    });
  }

  /** Mede os cards e cria cópias suficientes para não deixar espaços no loop. */
  rebuild() {
    this.track.querySelectorAll('[data-video-review-generated-group]').forEach((group) => group.remove());
    const sourceSlides = Array.from(this.sourceGroup.children)
      .filter((slide) => slide.matches('[data-video-review-slide]:not([hidden])'));

    // Sem cards configurados, a instância não reserva espaço na vitrine.
    if (sourceSlides.length === 0) {
      this.hidden = true;
      this.classList.remove('is-ready');
      return;
    }

    // Uma inclusão feita no editor torna a instância visível imediatamente.
    this.hidden = false;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const configuredColumns = Number.parseInt(
      isMobile ? this.dataset.columnsMobile : this.dataset.columnsDesktop,
      10,
    );
    const columns = Math.max(1, configuredColumns || 1);
    const gap = Number.parseFloat(this.dataset.gap) || 0;
    const carouselSize = Math.max(50, Number.parseInt(this.dataset.carouselSize, 10) || 100);
    const baseSlideWidth = (this.viewport.clientWidth - ((columns - 1) * gap)) / columns;
    const slideWidth = Math.max(1, baseSlideWidth * (carouselSize / 100));
    this.style.setProperty('--vr-slide-width', `${slideWidth}px`);

    const groupWidth = this.sourceGroup.getBoundingClientRect().width;

    // Uma seção ainda oculta no editor será medida novamente pelo observer.
    if (groupWidth <= 0) {
      this.classList.remove('is-ready');
      return;
    }

    const distance = groupWidth + gap;
    const requiredCopies = Math.max(1, Math.ceil((this.viewport.clientWidth * 2) / distance));

    // Cópias extras mantêm a janela preenchida mesmo com somente um vídeo.
    for (let copyIndex = 0; copyIndex < requiredCopies; copyIndex += 1) {
      const clone = this.sourceGroup.cloneNode(true);
      clone.removeAttribute('data-video-review-source-group');
      clone.setAttribute('data-video-review-generated-group', '');
      clone.setAttribute('aria-hidden', 'true');
      // Templates pertencem somente ao grupo original e não devem ser duplicados.
      clone.querySelectorAll('template[data-video-review-template]').forEach((template) => template.remove());
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('button').forEach((button) => button.setAttribute('tabindex', '-1'));
      this.track.appendChild(clone);
    }

    // Links diretos não possuem um objeto Liquid com aspect_ratio.
    // A proporção real é aplicada assim que os metadados do arquivo chegam.
    this.syncVideoAspectRatios();

    const configuredSpeed = Number.parseInt(this.dataset.speed, 10) || 4;
    const pixelsPerSecond = 20 + (configuredSpeed * 14);
    const duration = Math.max(distance / pixelsPerSecond, 4);
    this.style.setProperty('--vr-translate-distance', `${-distance}px`);
    this.style.setProperty('--vr-duration', `${duration}s`);
    this.restartAnimation();
    this.playPreviewVideos();
  }

  /** Aplica a proporção real às fontes diretas e também às cópias do loop. */
  syncVideoAspectRatios() {
    this.querySelectorAll('.video-review-carousel__preview-video').forEach((video) => {
      // A propriedade JavaScript não é clonada, evitando perder listeners nos clones.
      if (!video.videoReviewRatioBound) {
        video.videoReviewRatioBound = true;
        video.addEventListener('loadedmetadata', () => this.applyVideoAspectRatio(video), { once: true });
      }

      // Vídeos já carregados podem ser dimensionados sem aguardar outro evento.
      this.applyVideoAspectRatio(video);
    });
  }

  /** Atualiza somente o card cujo vídeo já possui dimensões utilizáveis. */
  applyVideoAspectRatio(video) {
    // Metadados incompletos mantêm temporariamente a proporção padrão do CSS.
    if (!video.videoWidth || !video.videoHeight) return;

    const media = video.closest('.video-review-carousel__media');

    // Uma prévia removida durante uma atualização do editor não recebe estilo.
    if (!media) return;

    media.style.setProperty('--vr-video-aspect-ratio', `${video.videoWidth} / ${video.videoHeight}`);
  }

  /** Inicia os vídeos mudos da vitrine, inclusive as cópias do loop. */
  playPreviewVideos() {
    // Quando a opção está desligada, os cards continuam usando imagens estáticas.
    if (this.dataset.previewAutoplay !== 'true') return;

    // Uma reconstrução por resize não pode reiniciar vídeos atrás do modal.
    if (this.classList.contains('is-modal-open')) {
      // Clones recém-criados carregam autoplay e precisam ser pausados também.
      this.pausePreviewVideos();
      return;
    }

    this.previewPlaybackPaused = false;

    this.querySelectorAll('.video-review-carousel__preview-video').forEach((video) => {
      // Autoplay confiável exige que o estado mudo também seja aplicado por JS.
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');

      // Recriações do editor podem interromper o autoplay nativo.
      video.play().catch(() => {
        // Se a mídia ainda não estiver pronta, tenta novamente uma única vez.
        video.addEventListener('canplay', () => {
          // Um modal aberto durante o carregamento mantém a prévia pausada.
          if (!this.previewPlaybackPaused) video.play().catch(() => undefined);
        }, { once: true });
      });
    });
  }

  /** Pausa somente as prévias do carrossel enquanto o modal está aberto. */
  pausePreviewVideos() {
    this.previewPlaybackPaused = true;
    this.querySelectorAll('.video-review-carousel__preview-video').forEach((video) => video.pause());
  }

  /** Reinicia os keyframes para aplicar imediatamente as novas medidas. */
  restartAnimation() {
    this.classList.remove('is-ready');
    void this.track.offsetWidth;
    this.classList.add('is-ready');
  }

  /** Abre o vídeo correspondente ao card original ou a uma cópia visual. */
  handleTrackClick(event) {
    const trigger = event.target.closest('[data-video-review-open]');

    // Cliques fora de cards configurados preservam o comportamento da página.
    if (!trigger || trigger.disabled) return;

    const videoIndex = trigger.dataset.videoReviewOpen;

    // Chaves vazias não podem abrir um template inexistente.
    if (!videoIndex) return;

    this.openVideo(videoIndex);
  }

  /** Abre o dialog nativo e carrega somente o vídeo selecionado. */
  openVideo(videoIndex) {
    const video = this.renderVideo(videoIndex);

    // O modal só pode abrir quando o template do vídeo existe.
    if (!video) return;

    this.classList.add('is-modal-open');
    document.documentElement.classList.add('video-review-modal-open');
    this.pausePreviewVideos();

    // Dialog nativo fornece foco, Escape e top layer; o atributo é fallback.
    if (typeof this.modal.showModal === 'function') {
      this.modal.showModal();
    }
    else {
      this.modal.setAttribute('open', '');
    }

    // Reprodução automática é opcional e pode ser bloqueada pelo navegador.
    if (this.dataset.modalAutoplay === 'true') {
      video.play().catch(() => undefined);
    }
  }

  /** Troca o conteúdo do modal sem carregar todos os vídeos antecipadamente. */
  renderVideo(videoIndex) {
    const indexes = this.getVideoIndexes();

    // Sem vídeos configurados não existe conteúdo ampliável.
    if (indexes.length === 0) return null;

    const normalizedIndex = indexes.includes(videoIndex) ? videoIndex : indexes[0];
    const template = this.getVideoTemplates()
      .find((candidate) => candidate.dataset.videoReviewTemplate === normalizedIndex);

    // Um template ausente indica uma renderização incompleta do editor.
    if (!template) return null;

    this.currentVideoIndex = normalizedIndex;
    this.modalContent.replaceChildren(template.content.cloneNode(true));
    const video = this.modalContent.querySelector('video');

    // O bloco configurado deve produzir um elemento de vídeo válido.
    if (!video) return null;

    video.loop = this.dataset.modalLoop === 'true';
    video.muted = this.dataset.modalMuted === 'true';
    video.defaultMuted = video.muted;
    video.controls = true;
    this.modalCaption.textContent = template.dataset.videoLabel || '';
    this.modalCounter.textContent = `${indexes.indexOf(normalizedIndex) + 1} / ${indexes.length}`;
    this.modalCounter.hidden = this.dataset.showCounter !== 'true';
    this.previousButton.hidden = indexes.length < 2;
    this.nextButton.hidden = indexes.length < 2;
    return video;
  }

  /** Retorna as chaves configuradas na mesma ordem dos blocos no editor. */
  getVideoIndexes() {
    return this.getVideoTemplates()
      .map((template) => template.dataset.videoReviewTemplate)
      .filter(Boolean);
  }

  /** Localiza somente os templates originais, nunca os grupos visuais clonados. */
  getVideoTemplates() {
    return Array.from(this.querySelectorAll('[data-video-review-template]'))
      .filter((template) => !template.closest('[data-video-review-generated-group]'));
  }

  /** Exibe o vídeo anterior e mantém navegação circular. */
  handlePrevious() {
    this.navigateModal(-1);
  }

  /** Exibe o próximo vídeo e mantém navegação circular. */
  handleNext() {
    this.navigateModal(1);
  }

  /** Calcula a posição seguinte e substitui o vídeo atual. */
  navigateModal(direction) {
    const indexes = this.getVideoIndexes();
    const currentPosition = indexes.indexOf(this.currentVideoIndex);

    // Navegação não é necessária quando existe menos de dois vídeos.
    if (indexes.length < 2 || currentPosition < 0) return;

    const nextPosition = (currentPosition + direction + indexes.length) % indexes.length;
    const video = this.renderVideo(indexes[nextPosition]);

    // O autoplay continua respeitando a configuração após a troca.
    if (video && this.dataset.modalAutoplay === 'true') {
      video.play().catch(() => undefined);
    }
  }

  /** Fecha o modal ao clicar no fundo; cliques no conteúdo não interferem. */
  handleModalClick(event) {
    // O próprio dialog é o backdrop quando o alvo não está na superfície.
    if (event.target === this.modal) this.closeModal();
  }

  /** Trata Escape e setas quando o modal está aberto. */
  handleKeyDown(event) {
    // Atalhos globais só atuam sobre a instância com dialog aberto.
    if (!this.modal.hasAttribute('open')) return;

    // Escape usa o fechamento nativo do dialog e apenas recebe fallback.
    if (event.key === 'Escape' && typeof this.modal.close !== 'function') {
      this.closeModal();
    }
    else if (event.key === 'ArrowLeft') {
      this.handlePrevious();
    }
    else if (event.key === 'ArrowRight') {
      this.handleNext();
    }
  }

  /** Fecha o dialog por botão ou fallback de navegador. */
  closeModal() {
    // O método nativo dispara o evento close e centraliza a limpeza.
    if (typeof this.modal.close === 'function' && this.modal.open) {
      this.modal.close();
    }
    else {
      this.modal.removeAttribute('open');
      this.handleModalClose();
    }
  }

  /** Interrompe mídia e libera o scroll após qualquer forma de fechamento. */
  handleModalClose(event) {
    // O clique no botão precisa iniciar o fechamento antes da limpeza.
    if (event?.currentTarget?.matches?.('[data-video-review-close]')) {
      this.closeModal();
      return;
    }

    this.modalContent.querySelectorAll('video').forEach((video) => video.pause());
    this.modalContent.replaceChildren();
    this.classList.remove('is-modal-open');
    this.playPreviewVideos();
    this.releasePageScroll();
  }

  /** Remove o bloqueio global somente quando nenhum outro modal está aberto. */
  releasePageScroll() {
    // Duas seções podem existir; uma ainda aberta deve manter o bloqueio.
    if (!document.querySelector('.video-review-carousel__modal[open]')) {
      document.documentElement.classList.remove('video-review-modal-open');
    }
  }
  }

  // A definição ocorre somente na primeira execução do asset nesta página.
  customElements.define('video-review-carousel', VideoReviewCarousel);
}
